/**
 * @zendir/ui - GroundTrackMap Leaflet Provider
 *
 * Leaflet-based 2D world map for ground tracks, stations, and terminator.
 * Uses Zendir hybrid theme; all interactions enabled (zoom, scroll, drag, etc.).
 * Import leaflet-zendir.css when using this component.
 */

import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTheme } from "../theme";
import type { GroundStation } from "../types";
import type {
  SatelliteTrack,
  GroundStationEnhanced,
  SROGroundStation,
  MapPin,
  LightSource,
} from "./GroundTrackMap";
import type { MapLayerDef } from "./GroundTrackMap";

// Optional Leaflet — consumer must install leaflet when using mapProvider="leaflet"
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./leaflet-zendir.css";
import {
  splitPolylineAtAntimeridian,
  segmentsWithWorldCopies,
  geodesicCirclePointsContinuous,
} from "./groundTrackMapLeafletUtils";
import {
  DEFAULT_TILE,
  FALLBACK_TILE,
  TILE_ERROR_THRESHOLD,
  OSM_ATTRIBUTION,
  TILE_PRESETS,
  CANVAS_LABEL_TILES,
  getBasemapAttribution,
} from "./groundTrackMapLeafletTiles";

const STATUS_COLOR_MAP: Record<string, string> = {
  normal: "#56f000",
  standby: "#2dccff",
  caution: "#fce83a",
  serious: "#ffb302",
  critical: "#ff3838",
  off: "#a4abb6",
};

const DEFAULT_TRACK_COLORS = [
  "#2dccff",
  "#3E3CFF",
  "#9D70FF",
  "#56f000",
  "#fce83a",
  "#ff7849",
  "#2dd4bf",
  "#ff3838",
];

// =============================================================================
// Icon helpers — produce L.DivIcon with inline SVG (no external assets needed)
// =============================================================================

/** Satellite spacecraft icon with solar panels, matching Astro UXDS style. */
function createSatDivIcon(statusColor: string, trackColor: string): L.DivIcon {
  // Status badge shape (Astro UXDS spec)
  const badge =
    statusColor === "#56f000"
      ? `<circle cx="7" cy="7" r="3" fill="${statusColor}"/>` // normal: filled dot
      : statusColor === "#2dccff"
        ? `<circle cx="7" cy="7" r="2.5" fill="none" stroke="${statusColor}" stroke-width="1.5"/>` // standby: ring
        : statusColor === "#fce83a"
          ? `<rect x="4.5" y="4.5" width="5" height="5" fill="${statusColor}"/>` // caution: square
          : statusColor === "#ffb302"
            ? `<polygon points="7,3.5 10.5,7 7,10.5 3.5,7" fill="${statusColor}"/>` // serious: diamond (Astro UXDS)
            : statusColor === "#ff3838"
              ? `<polygon points="7,10 4,4 10,4" fill="${statusColor}"/>` // critical: triangle down
              : `<circle cx="7" cy="7" r="2" fill="${statusColor}"/>`; // off: small dot

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <!-- Glow ring -->
    <circle cx="17" cy="17" r="15" fill="${statusColor}" fill-opacity="0.12"/>
    <!-- Main body circle -->
    <circle cx="17" cy="17" r="11" fill="#0d1323" stroke="${trackColor}" stroke-width="1.5"/>
    <!-- Left solar panel -->
    <rect x="2" y="15" width="9" height="4" rx="0.5" fill="${trackColor}" fill-opacity="0.75"/>
    <line x1="5.5" y1="15" x2="5.5" y2="19" stroke="${trackColor}" stroke-width="0.5" stroke-opacity="0.45"/>
    <!-- Connector left -->
    <line x1="11" y1="17" x2="13" y2="17" stroke="${trackColor}" stroke-width="1.2"/>
    <!-- Satellite bus body -->
    <rect x="13" y="14.5" width="8" height="5" rx="1" fill="${statusColor}"/>
    <!-- Connector right -->
    <line x1="21" y1="17" x2="23" y2="17" stroke="${trackColor}" stroke-width="1.2"/>
    <!-- Right solar panel -->
    <rect x="23" y="15" width="9" height="4" rx="0.5" fill="${trackColor}" fill-opacity="0.75"/>
    <line x1="26.5" y1="15" x2="26.5" y2="19" stroke="${trackColor}" stroke-width="0.5" stroke-opacity="0.45"/>
    <!-- Status badge (top-left) -->
    <circle cx="7" cy="7" r="5.5" fill="#0d1323"/>
    ${badge}
  </svg>`;

  return L.divIcon({
    html: svg,
    className: "zendir-sat-icon",
    iconSize: [34, 34] as unknown as L.PointExpression,
    iconAnchor: [17, 17] as unknown as L.PointExpression,
    tooltipAnchor: [0, -18] as unknown as L.PointExpression,
  });
}

/** Ground station icon — dish/antenna shape, matching Astro UXDS style. */
function createStationDivIcon(
  statusColor: string,
  type: string,
  status: string,
): L.DivIcon {
  // Status badge
  const badge =
    status === "normal"
      ? `<circle cx="5" cy="5" r="2.5" fill="${statusColor}"/>`
      : status === "standby"
        ? `<circle cx="5" cy="5" r="2" fill="none" stroke="${statusColor}" stroke-width="1.2"/>`
        : status === "caution"
          ? `<rect x="3" y="3" width="4" height="4" fill="${statusColor}"/>`
          : status === "serious"
            ? `<polygon points="5,2.5 7.5,5 5,7.5 2.5,5" fill="${statusColor}"/>`
            : status === "critical"
              ? `<polygon points="5,7.5 2.5,2.5 7.5,2.5" fill="${statusColor}"/>`
              : `<circle cx="5" cy="5" r="1.5" fill="${statusColor}"/>`;

  let iconInner = "";
  if (type === "phased-array") {
    // Grid pattern (phased array)
    iconInner = `
      <rect x="9" y="7" width="10" height="8" rx="0.5" fill="none" stroke="${statusColor}" stroke-width="1.2"/>
      <line x1="12" y1="7" x2="12" y2="15" stroke="${statusColor}" stroke-width="0.7"/>
      <line x1="15" y1="7" x2="15" y2="15" stroke="${statusColor}" stroke-width="0.7"/>
      <line x1="9" y1="10" x2="19" y2="10" stroke="${statusColor}" stroke-width="0.7"/>
      <line x1="9" y1="13" x2="19" y2="13" stroke="${statusColor}" stroke-width="0.7"/>
      <line x1="14" y1="15" x2="14" y2="20" stroke="${statusColor}" stroke-width="1.2"/>
      <line x1="11" y1="20" x2="17" y2="20" stroke="${statusColor}" stroke-width="1.2"/>`;
  } else if (type === "relay") {
    // Relay dish with signal arcs
    iconInner = `
      <path d="M 8 16 a 7 7 0 0 1 7 -7" fill="none" stroke="${statusColor}" stroke-width="1.5" stroke-linecap="round"/>
      <path d="M 8 16 m 2 -2 a 5 5 0 0 1 5 -5" fill="none" stroke="${statusColor}" stroke-width="1.2" stroke-linecap="round"/>
      <line x1="14" y1="16" x2="14" y2="21" stroke="${statusColor}" stroke-width="1.2"/>
      <line x1="11" y1="21" x2="17" y2="21" stroke="${statusColor}" stroke-width="1.2"/>
      <path d="M 16 8 a 3 3 0 0 1 0 4" fill="none" stroke="${statusColor}" stroke-width="1.2" stroke-linecap="round"/>
      <path d="M 17.5 6.5 a 5 5 0 0 1 0 7" fill="none" stroke="${statusColor}" stroke-width="1" stroke-linecap="round"/>`;
  } else {
    // Default: dish antenna with concentric arcs (Astro UXDS antenna pattern)
    iconInner = `
      <path d="M 14 14 m -7 0 a 7 7 0 0 1 7 -7" fill="none" stroke="${statusColor}" stroke-width="1.5" stroke-linecap="round"/>
      <path d="M 14 14 m -5 0 a 5 5 0 0 1 5 -5" fill="none" stroke="${statusColor}" stroke-width="1.5" stroke-linecap="round"/>
      <path d="M 14 14 m -3 0 a 3 3 0 0 1 3 -3" fill="none" stroke="${statusColor}" stroke-width="1.5" stroke-linecap="round"/>
      <circle cx="14" cy="14" r="1.5" fill="${statusColor}"/>
      <line x1="14" y1="15" x2="14" y2="21" stroke="${statusColor}" stroke-width="1.2"/>
      <line x1="11" y1="21" x2="17" y2="21" stroke="${statusColor}" stroke-width="1.2"/>`;
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
    <!-- Glow ring -->
    <circle cx="14" cy="14" r="13" fill="${statusColor}" fill-opacity="0.1"/>
    <!-- Background -->
    <circle cx="14" cy="14" r="11" fill="#0d1323" stroke="${statusColor}" stroke-width="1.2" stroke-opacity="0.7"/>
    ${iconInner}
    <!-- Status badge (top-left) -->
    <circle cx="5" cy="5" r="5" fill="#0d1323"/>
    ${badge}
  </svg>`;

  return L.divIcon({
    html: svg,
    className: "zendir-station-icon",
    iconSize: [28, 28] as unknown as L.PointExpression,
    iconAnchor: [14, 14] as unknown as L.PointExpression,
    tooltipAnchor: [0, -14] as unknown as L.PointExpression,
  });
}

/**
 * Calculate the sub-solar point (latitude, longitude) for a given UTC Date.
 * Returns the geographic location where the sun is directly overhead.
 */
function calculateSubSolarPoint(date: Date): [number, number] {
  const dayOfYear = Math.floor(
    (date.getTime() - Date.UTC(date.getUTCFullYear(), 0, 0)) / 86400000,
  );
  const declination =
    -23.44 * Math.cos(((2 * Math.PI) / 365) * (dayOfYear + 10));
  const B = ((2 * Math.PI) / 365) * (dayOfYear - 81);
  const eotMinutes =
    9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
  const utcHours =
    date.getUTCHours() +
    date.getUTCMinutes() / 60 +
    date.getUTCSeconds() / 3600;
  const solarHours = utcHours + eotMinutes / 60;
  const lon = -(solarHours - 12) * 15;
  return [declination, ((lon + 540) % 360) - 180];
}

/**
 * Calculate the approximate sub-lunar point for a given UTC Date.
 * Uses a simplified low-precision algorithm sufficient for map overlay rendering.
 */
function calculateSubLunarPoint(date: Date): [number, number] {
  const J2000 = Date.UTC(2000, 0, 1, 12, 0, 0);
  const d = (date.getTime() - J2000) / 86400000;
  const DEG = Math.PI / 180;
  const L = (218.316 + 13.176396 * d) % 360;
  const M = (134.963 + 13.064993 * d) % 360;
  const F = (93.272 + 13.22935 * d) % 360;
  const lon_ecl = L + 6.289 * Math.sin(M * DEG);
  const lat_ecl = 5.128 * Math.sin(F * DEG);
  const obliquity = 23.439 - 0.00000036 * d;
  const sinRA =
    Math.sin(lon_ecl * DEG) * Math.cos(obliquity * DEG) -
    Math.tan(lat_ecl * DEG) * Math.sin(obliquity * DEG);
  const cosRA = Math.cos(lon_ecl * DEG);
  let RA = Math.atan2(sinRA, cosRA) / DEG;
  if (RA < 0) RA += 360;
  const dec =
    Math.asin(
      Math.sin(lat_ecl * DEG) * Math.cos(obliquity * DEG) +
        Math.cos(lat_ecl * DEG) *
          Math.sin(obliquity * DEG) *
          Math.sin(lon_ecl * DEG),
    ) / DEG;
  const GMST = (280.46061837 + 360.98564736629 * d) % 360;
  let geoLon = RA - GMST;
  geoLon = ((geoLon + 540) % 360) - 180;
  return [dec, geoLon];
}

/**
 * Compute a terminator line with CONTINUOUS (unwrapped) longitudes.
 *
 * `depressionDeg` offsets the solar zenith angle to produce twilight
 * boundaries instead of the geometric sunset line:
 *   - 0° = geometric sunset/sunrise (day/night boundary)
 *   - 6° = civil twilight (horizon still visible, outdoor activities possible)
 *   - 12° = nautical twilight (horizon barely visible, stars appearing)
 *   - 18° = astronomical twilight (sky fully dark for observation)
 *
 * These thresholds follow IAU/USNO standard definitions used in celestial
 * navigation, Astro UX space ops dashboards, and STK/GMAT mission tools.
 */
function calculateTerminatorContinuous(
  date: Date,
  depressionDeg: number = 0,
  numPoints: number = 360,
): { sunset: Array<[number, number]>; sunrise: Array<[number, number]> } {
  const dayOfYear = Math.floor(
    (date.getTime() - Date.UTC(date.getUTCFullYear(), 0, 0)) / 86400000,
  );
  const declination =
    -23.44 * Math.cos(((2 * Math.PI) / 365) * (dayOfYear + 10));
  const decRad = (declination * Math.PI) / 180;

  // Equation of Time correction (minutes) for more accurate solar position
  const B = ((2 * Math.PI) / 365) * (dayOfYear - 81);
  const eotMinutes =
    9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);

  // Sub-solar longitude: the geographic longitude where the sun is directly overhead.
  // At noon UTC (solarHours=12) the sun is over Greenwich (0°).
  // At midnight UTC (solarHours=0) the sun is over the dateline (180°).
  const utcHours =
    date.getUTCHours() +
    date.getUTCMinutes() / 60 +
    date.getUTCSeconds() / 3600;
  const solarHours = utcHours + eotMinutes / 60;
  const subSolarLon = -(solarHours - 12) * 15;
  const depRad = (depressionDeg * Math.PI) / 180;

  const sunset: Array<[number, number]> = [];
  const sunrise: Array<[number, number]> = [];

  for (let i = 0; i <= numPoints; i++) {
    // Avoid exact +/- 90 to prevent longitude singularities at the poles
    const lat = -89.999 + (i / numPoints) * 179.998;
    const latRad = lat * (Math.PI / 180);
    const cosH =
      -(Math.sin(depRad) + Math.sin(latRad) * Math.sin(decRad)) /
      (Math.cos(latRad) * Math.cos(decRad));

    if (cosH < -1) {
      // All day — midnight sun at this latitude. No night boundary points.
    } else if (cosH > 1) {
      // All night — polar night at this latitude. Night spans all longitudes.
      sunset.push([lat, subSolarLon]);
      sunrise.push([lat, subSolarLon + 360]);
    } else {
      const H = (Math.acos(cosH) * 180) / Math.PI;
      // Sunset boundary: H degrees EAST of the sub-solar point (afternoon side).
      // Observers here see the sun setting in the west — entering night.
      sunset.push([lat, subSolarLon + H]);
      // Sunrise boundary: H degrees WEST of the sub-solar point (morning side),
      // expressed as subSolarLon - H + 360 to keep values > sunset for a continuous polygon.
      sunrise.push([lat, subSolarLon + 360 - H]);
    }
  }

  return { sunset, sunrise };
}

/**
 * Build the full night polygon, properly closing the loops around the poles.
 * Ensures longitudes are strictly continuous so the map projection doesn't
 * draw antimeridian lines across the day side.
 */
function buildNightPolygon(terminator: {
  sunset: Array<[number, number]>;
  sunrise: Array<[number, number]>;
}): [number, number][] {
  const { sunset, sunrise } = terminator;
  if (!sunset.length || !sunrise.length) return [];

  const poly: [number, number][] = [];

  // Left edge of night (sunset): South to North
  poly.push(...sunset);

  // Right edge of night (sunrise): North to South
  poly.push(...[...sunrise].reverse());

  // Leaflet will automatically close the polygon by connecting the last point
  // (South end of sunrise) back to the first point (South end of sunset).
  // Because longitudes are continuous and bounded (sunrise.lon - sunset.lon <= 360),
  // this closing line will simply span the bottom or top of the map, perfectly
  // enclosing the night area without crossing the day side!

  return poly;
}

export type GroundStationMapItem =
  | GroundStation
  | GroundStationEnhanced
  | SROGroundStation;

export interface GroundTrackMapLeafletProps {
  allSatellites: SatelliteTrack[];
  groundStations: GroundStationMapItem[];
  showTerminator?: boolean;
  /** Override the time used for the terminator calculation (defaults to wall-clock now). */
  terminatorTime?: Date;
  showGrid?: boolean;
  showLegend?: boolean;
  showEquator?: boolean;
  showRecenterButton?: boolean;
  /** Show a toggle button to switch between Dark and Satellite tile styles. Default true. */
  showMapStyleToggle?: boolean;
  defaultCenter?: [number, number];
  defaultZoom?: number;
  height?: number | string;
  width?: number | string;
  minHeight?: string;
  emptyMessage?: string;
  tileUrl?: string;
  /**
   * URL template for a "night" tile layer (e.g. NASA Black Marble, VIIRS city lights).
   * Rendered beneath the terminator overlay so it only appears on the dark side.
   * Requires `showTerminator`. Falls back to dark overlay when not provided.
   */
  nightTileUrl?: string;
  /**
   * Point light sources rendered on the night side (masked by the terminator).
   * Each light fades in through twilight and reaches full brightness at night.
   */
  lightSources?: LightSource[];
  className?: string;
  onSatelliteClick?: (id: string) => void;
  onStationClick?: (id: string) => void;
  /** Points-of-interest pins */
  pins?: MapPin[];
  /** Allow adding/editing pins via map clicks */
  pinsEditable?: boolean;
  onPinAdd?: (pin: Omit<MapPin, "id">) => void;
  onPinUpdate?: (pin: MapPin) => void;
  onPinRemove?: (pinId: string) => void;
  /** Custom overlay layers for the Layers panel */
  customLayers?: MapLayerDef[];
  /** Called when any layer is toggled */
  onLayerChange?: (layerId: string, enabled: boolean) => void;
  /**
   * Show sun and moon position markers on the map.
   * Requires `showTerminator`. Default: true when terminator is enabled.
   */
  showCelestialMarkers?: boolean;
}

export function GroundTrackMapLeaflet({
  allSatellites,
  groundStations,
  showTerminator = true,
  terminatorTime,
  showGrid = false,
  showLegend = true,
  showEquator = false,
  showRecenterButton = true,
  showMapStyleToggle = true,
  defaultCenter = [20, 0],
  defaultZoom = 2,
  height = "100%",
  width = "100%",
  minHeight = "400px",
  emptyMessage = "No orbital data available",
  tileUrl,
  nightTileUrl,
  lightSources,
  className = "",
  onSatelliteClick,
  onStationClick,
  pins,
  pinsEditable = false,
  onPinAdd,
  onPinUpdate,
  onPinRemove,
  customLayers,
  onLayerChange,
  showCelestialMarkers,
}: GroundTrackMapLeafletProps): React.ReactElement {
  const { tokens } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const labelTileLayerRef = useRef<L.TileLayer | null>(null);
  const overlayGroupRef = useRef<L.LayerGroup | null>(null);
  const controlsRef = useRef<L.Control[]>([]);
  /** Separate layer group so pin updates don't clear satellite/station layers */
  const pinsGroupRef = useRef<L.LayerGroup | null>(null);
  const [ready, setReady] = useState(false);

  // ── Layers panel open/close ──
  const [layersPanelOpen, setLayersPanelOpen] = useState(false);
  const layersPanelRef = useRef<HTMLDivElement>(null);

  // Close layers panel when clicking outside
  useEffect(() => {
    if (!layersPanelOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        layersPanelRef.current &&
        !layersPanelRef.current.contains(e.target as Node)
      ) {
        setLayersPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [layersPanelOpen]);

  // ── Tile style with localStorage persistence ──
  const TILE_STORAGE_KEY = "zendir-map-tile-style";
  const isExplicitTileUrl = tileUrl !== undefined;
  const [tileStyle, setTileStyle] = useState<"dark" | "satellite">(() => {
    if (isExplicitTileUrl) return "dark";
    try {
      const saved = localStorage.getItem(TILE_STORAGE_KEY);
      if (saved === "dark" || saved === "satellite") return saved;
    } catch {
      /* SSR / storage blocked */
    }
    return "dark";
  });

  const handleTileStyleChange = useCallback((style: "dark" | "satellite") => {
    setTileStyle(style);
    try {
      localStorage.setItem(TILE_STORAGE_KEY, style);
    } catch {
      /* noop */
    }
  }, []);

  const effectiveTileUrl = isExplicitTileUrl
    ? tileUrl
    : TILE_PRESETS[tileStyle];

  // ── Internal overlay toggle state (built-in layers: terminator, grid) ──
  const [internalTerminator, setInternalTerminator] = useState(showTerminator);
  const [internalGrid, setInternalGrid] = useState(showGrid);

  // Sync with prop changes (props are the initial source of truth)
  useEffect(() => {
    setInternalTerminator(showTerminator);
  }, [showTerminator]);
  useEffect(() => {
    setInternalGrid(showGrid);
  }, [showGrid]);

  // ── Custom layer toggle state ──
  const [customLayerState, setCustomLayerState] = useState<
    Record<string, boolean>
  >(() => {
    const state: Record<string, boolean> = {};
    for (const l of customLayers ?? []) {
      state[l.id] = l.defaultEnabled ?? true;
    }
    return state;
  });

  // Sync when customLayers prop changes (add new layers with defaults)
  useEffect(() => {
    setCustomLayerState((prev) => {
      const next = { ...prev };
      for (const l of customLayers ?? []) {
        if (!(l.id in next)) next[l.id] = l.defaultEnabled ?? true;
      }
      return next;
    });
  }, [customLayers]);

  const handleLayerToggle = useCallback(
    (layerId: string, enabled: boolean) => {
      if (layerId === "terminator") {
        setInternalTerminator(enabled);
      } else if (layerId === "grid") {
        setInternalGrid(enabled);
      } else {
        setCustomLayerState((prev) => ({ ...prev, [layerId]: enabled }));
      }
      onLayerChange?.(layerId, enabled);
    },
    [onLayerChange],
  );

  const clearLayers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    overlayGroupRef.current?.clearLayers();
    controlsRef.current.forEach((ctrl) => map.removeControl(ctrl));
    controlsRef.current = [];
  }, []);

  const addLayer = useCallback((layer: L.Layer) => {
    overlayGroupRef.current?.addLayer(layer);
  }, []);

  // Create map once on mount (or when tileUrl changes). Intentionally not depending on
  // defaultCenter/defaultZoom so animated updates do not recreate the map (no flicker/zoom reset).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultCenter/defaultZoom used only for initial view
    const el = containerRef.current;
    if (!el) return;

    const center: L.LatLngExpression = defaultCenter ?? [20, 0];
    const zoom = defaultZoom ?? 2;

    const map = L.map(el, {
      center,
      zoom,
      zoomControl: false,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      touchZoom: true,
      boxZoom: true,
      keyboard: true,
      dragging: true,
      attributionControl: true,
      // Canvas renderer for vector layers (tracks, footprints, terminator).
      // The overlay set is torn down and rebuilt whenever track data or the
      // terminator time changes; as SVG that churn creates/destroys hundreds
      // of DOM nodes per rebuild, which Safari's compositor handles poorly —
      // iPad tabs run out of memory and desktop Safari freezes on
      // click-zoom. Canvas draws sidestep the DOM entirely.
      preferCanvas: true,
      maxBounds: [
        [-90, -540],
        [90, 540],
      ],
      maxBoundsViscosity: 1.0,
    });

    map.attributionControl.setPrefix("");

    const overlayGroup = L.layerGroup();
    overlayGroup.addTo(map);
    overlayGroupRef.current = overlayGroup;

    const pinsGroup = L.layerGroup();
    pinsGroup.addTo(map);
    pinsGroupRef.current = pinsGroup;

    mapRef.current = map;
    setReady(true);

    const onSize = () => {
      map.invalidateSize({ animate: false });
    };
    // Immediate RAF + two deferred calls cover: first paint, CSS transitions, flex layout settle
    const raf = requestAnimationFrame(onSize);
    const t1 = setTimeout(onSize, 150);
    const t2 = setTimeout(onSize, 500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      cancelAnimationFrame(raf);
      clearLayers();
      pinsGroupRef.current?.clearLayers();
      pinsGroupRef.current = null;
      tileLayerRef.current = null;
      labelTileLayerRef.current = null;
      map.remove();
      mapRef.current = null;
      overlayGroupRef.current = null;
    };
    // Mount-only: tile layer is swapped by a dedicated effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearLayers]);

  // Swap tile layer without recreating the entire map (avoids flash on style toggle).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    // Remove existing tile layer (Leaflet drops that layer's attribution with it)
    if (tileLayerRef.current) {
      tileLayerRef.current.remove();
      tileLayerRef.current = null;
    }
    if (labelTileLayerRef.current) {
      labelTileLayerRef.current.remove();
      labelTileLayerRef.current = null;
    }

    const isCartoTiles = effectiveTileUrl.includes("cartocdn");
    const basemapAttribution = getBasemapAttribution(effectiveTileUrl);

    const tile = L.tileLayer(effectiveTileUrl, {
      maxZoom: 19,
      subdomains: isCartoTiles ? "abcd" : "abc",
      crossOrigin: true,
      attribution: basemapAttribution || undefined,
    });
    tile.addTo(map);
    tile.bringToBack();
    tileLayerRef.current = tile;

    // Place names for basemaps that ship them as a separate reference layer
    const labelUrl = CANVAS_LABEL_TILES[effectiveTileUrl];
    if (labelUrl) {
      const labels = L.tileLayer(labelUrl, {
        maxZoom: 19,
        crossOrigin: true,
      });
      labels.addTo(map);
      tile.bringToBack();
      labelTileLayerRef.current = labels;
    }

    // Fallback on tile error
    let hasSwitchedToFallback = false;
    const onTileError = () => {
      if (hasSwitchedToFallback) return;
      hasSwitchedToFallback = true;
      tile.off("tileerror", onTileError);
      tile.remove();
      labelTileLayerRef.current?.remove();
      labelTileLayerRef.current = null;
      const fallback = L.tileLayer(FALLBACK_TILE, {
        maxZoom: 19,
        subdomains: "abc",
        crossOrigin: true,
        attribution: OSM_ATTRIBUTION,
      });
      fallback.addTo(map);
      fallback.bringToBack();
      tileLayerRef.current = fallback;
    };
    tile.on("tileerror", onTileError);
  }, [effectiveTileUrl, ready]);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    clearLayers();

    // === Night Tile Layer (placed beneath terminator so night imagery appears on dark side) ===
    if (nightTileUrl && internalTerminator) {
      const nightTile = L.tileLayer(nightTileUrl, {
        maxZoom: 19,
        crossOrigin: true,
        opacity: 0.7,
        className: "zendir-night-tiles",
      });
      addLayer(nightTile);
    }

    if (internalTerminator) {
      const now = terminatorTime ?? new Date();

      // Graduated terminator shade. Stronger on satellite imagery for contrast,
      // lighter on dark tiles where subtlety reads better.
      const isSatelliteTile = tileStyle === "satellite";
      const BAND_STEP = 1.5;
      const MAX_DEP = 30;
      const NIGHT_OPACITY = isSatelliteTile ? 0.75 : 0.38;
      const NIGHT_COLOR = isSatelliteTile ? "#030810" : "#2a3a52";
      const bandSteps: Array<{
        depression: number;
        opacity: number;
        color: string;
      }> = [];
      for (let d = 0; d <= MAX_DEP; d += BAND_STEP) {
        const t = Math.min(d / 18, 1);
        const opacity =
          Math.pow(t, 1.6) * (NIGHT_OPACITY * 0.85) +
          (d > 18 ? ((d - 18) / 12) * (NIGHT_OPACITY * 0.15) : 0);
        bandSteps.push({
          depression: d,
          opacity: Math.min(opacity, NIGHT_OPACITY),
          color: NIGHT_COLOR,
        });
      }
      bandSteps.push({
        depression: 90,
        opacity: NIGHT_OPACITY,
        color: NIGHT_COLOR,
      });

      let prevOpacity = 0;

      // Draw each band as a cumulative overlay — each adds incremental darkening
      // By stacking full night polygons with small opacities, we avoid drawing
      // complex band-difference polygons, which is much more robust at the poles.
      for (const b of bandSteps) {
        const terminator = calculateTerminatorContinuous(
          now,
          Math.min(b.depression, 89),
        );
        const bandOpacity = b.opacity - prevOpacity;
        if (bandOpacity < 0.002 || terminator.sunset.length === 0) {
          prevOpacity = b.opacity;
          continue;
        }

        const poly = buildNightPolygon(terminator);
        if (poly.length < 3) continue;

        [0, 360, -360].forEach((offset) => {
          const shifted = poly.map(
            ([lat, lon]) => [lat, lon + offset] as [number, number],
          );
          addLayer(
            L.polygon(shifted, {
              color: "transparent",
              fillColor: b.color,
              fillOpacity: bandOpacity,
              interactive: false,
            }),
          );
        });

        prevOpacity = b.opacity;
      }

      // Draw a smooth terminator boundary line (0° depression)
      // — a thin, continuous stroke showing the day/night geometric edge.
      // Styling adapts to tile style: brighter on satellite for visibility.
      const terminatorEdge = calculateTerminatorContinuous(now, 0);
      const glowColor = isSatelliteTile ? "#a0a0a0" : "#909090";
      const glowOpacity = isSatelliteTile ? 0.15 : 0.08;
      const coreColor = isSatelliteTile ? "#c0c0c0" : "#a8a8a8";
      const coreOpacity = isSatelliteTile ? 0.45 : 0.3;
      if (terminatorEdge.sunset.length > 2) {
        const sunsetLine = terminatorEdge.sunset.map(
          ([lat, lon]) => [lat, lon] as [number, number],
        );
        const sunriseLine = terminatorEdge.sunrise.map(
          ([lat, lon]) => [lat, lon] as [number, number],
        );
        [0, 360, -360].forEach((offset) => {
          addLayer(
            L.polyline(
              sunsetLine.map(
                ([lat, lon]) => [lat, lon + offset] as [number, number],
              ),
              {
                color: glowColor,
                weight: 3,
                opacity: glowOpacity,
                interactive: false,
                smoothFactor: 1.5,
              },
            ),
          );
          addLayer(
            L.polyline(
              sunriseLine.map(
                ([lat, lon]) => [lat, lon + offset] as [number, number],
              ),
              {
                color: glowColor,
                weight: 3,
                opacity: glowOpacity,
                interactive: false,
                smoothFactor: 1.5,
              },
            ),
          );
          addLayer(
            L.polyline(
              sunsetLine.map(
                ([lat, lon]) => [lat, lon + offset] as [number, number],
              ),
              {
                color: coreColor,
                weight: 1,
                opacity: coreOpacity,
                interactive: false,
                smoothFactor: 1.5,
              },
            ),
          );
          addLayer(
            L.polyline(
              sunriseLine.map(
                ([lat, lon]) => [lat, lon + offset] as [number, number],
              ),
              {
                color: coreColor,
                weight: 1,
                opacity: coreOpacity,
                interactive: false,
                smoothFactor: 1.5,
              },
            ),
          );
        });
      }

      // TODO: Re-enable sun/moon celestial markers once visual design is finalized.
      // Calculations (calculateSubSolarPoint, calculateSubLunarPoint) are correct.
      // Uncomment and set back to prop-driven to restore:
      //   const celestialEnabled = showCelestialMarkers !== undefined ? showCelestialMarkers : true;
      const celestialEnabled = false;
      if (celestialEnabled) {
        const [sunLat, sunLon] = calculateSubSolarPoint(now);
        const sunIcon = L.divIcon({
          className: "",
          iconSize: [28, 28] as unknown as L.PointExpression,
          iconAnchor: [14, 14] as unknown as L.PointExpression,
          html: `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
          <defs><filter id="sun-glow"><feGaussianBlur stdDeviation="1.5" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
          <circle cx="14" cy="14" r="6" fill="#FFB300" stroke="#FF8F00" stroke-width="1.2" filter="url(#sun-glow)"/>
          ${[0, 45, 90, 135, 180, 225, 270, 315]
            .map((a) => {
              const r1 = 8,
                r2 = 12,
                rad = (a * Math.PI) / 180;
              return `<line x1="${14 + r1 * Math.cos(rad)}" y1="${14 + r1 * Math.sin(rad)}" x2="${14 + r2 * Math.cos(rad)}" y2="${14 + r2 * Math.sin(rad)}" stroke="#FFB300" stroke-width="1.4" stroke-linecap="round" opacity="0.85"/>`;
            })
            .join("")}
        </svg>`,
        });
        const sunMarker = L.marker([sunLat, sunLon], {
          icon: sunIcon,
          interactive: true,
          zIndexOffset: 800,
        });
        sunMarker.bindTooltip(
          `<b>Sub-Solar Point</b><br/>Sun's zenith position<br/>Lat ${sunLat.toFixed(2)}° Lon ${sunLon.toFixed(2)}°`,
          {
            direction: "top",
            offset: [0, -14] as unknown as L.PointExpression,
            className: "zendir-celestial-tooltip",
          },
        );
        addLayer(sunMarker);

        // Sub-lunar point marker
        const [moonLat, moonLon] = calculateSubLunarPoint(now);
        const moonIcon = L.divIcon({
          className: "",
          iconSize: [22, 22] as unknown as L.PointExpression,
          iconAnchor: [11, 11] as unknown as L.PointExpression,
          html: `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
          <defs><filter id="moon-glow"><feGaussianBlur stdDeviation="1" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
          <circle cx="11" cy="11" r="6.5" fill="#e0e0e0" stroke="#9e9e9e" stroke-width="0.8" filter="url(#moon-glow)" opacity="0.9"/>
          <circle cx="9" cy="9.5" r="1.6" fill="#bdbdbd" opacity="0.5"/>
          <circle cx="13" cy="12" r="1.0" fill="#bdbdbd" opacity="0.4"/>
          <circle cx="10.5" cy="13.5" r="0.7" fill="#bdbdbd" opacity="0.35"/>
        </svg>`,
        });
        const moonMarker = L.marker([moonLat, moonLon], {
          icon: moonIcon,
          interactive: true,
          zIndexOffset: 790,
        });
        moonMarker.bindTooltip(
          `<b>Sub-Lunar Point</b><br/>Moon's zenith position<br/>Lat ${moonLat.toFixed(2)}° Lon ${moonLon.toFixed(2)}°`,
          {
            direction: "top",
            offset: [0, -11] as unknown as L.PointExpression,
            className: "zendir-celestial-tooltip",
          },
        );
        addLayer(moonMarker);
      } // end celestialEnabled
    }

    // === Light Sources (rendered as glowing markers on the night side) ===
    if (lightSources && lightSources.length > 0 && internalTerminator) {
      const now = terminatorTime ?? new Date();
      const dayOfYear = Math.floor(
        (now.getTime() - Date.UTC(now.getUTCFullYear(), 0, 0)) / 86400000,
      );
      const declination =
        -23.44 * Math.cos(((2 * Math.PI) / 365) * (dayOfYear + 10));
      const decRad = (declination * Math.PI) / 180;
      const lsSubSolarLon =
        -(now.getUTCHours() + now.getUTCMinutes() / 60 - 12) * 15;

      for (const light of lightSources) {
        const latRad = (light.latitude * Math.PI) / 180;
        const sinAlt =
          Math.sin(latRad) * Math.sin(decRad) +
          Math.cos(latRad) *
            Math.cos(decRad) *
            Math.cos(((light.longitude - lsSubSolarLon) * Math.PI) / 180);
        const solarAltDeg = (Math.asin(sinAlt) * 180) / Math.PI;

        let nightFactor: number;
        if (solarAltDeg >= 0) nightFactor = 0;
        else if (solarAltDeg <= -18) nightFactor = 1;
        else nightFactor = Math.min(1, -solarAltDeg / 18);

        if (nightFactor < 0.01) continue;

        const r = light.radius ?? 4;
        const intensity = light.intensity ?? 0.8;
        const color = light.color ?? "#ffeedd";
        const alpha = intensity * nightFactor;

        // Outer glow circle
        const glowMarker = L.circleMarker([light.latitude, light.longitude], {
          radius: r * 2,
          fillColor: color,
          fillOpacity: alpha * 0.4,
          color: color,
          weight: 0,
          interactive: !!light.label,
        });
        addLayer(glowMarker);

        // Bright core
        const coreMarker = L.circleMarker([light.latitude, light.longitude], {
          radius: Math.max(1, r * 0.5),
          fillColor: color,
          fillOpacity: Math.min(1, alpha * 1.2),
          color: "transparent",
          weight: 0,
          interactive: false,
        });
        addLayer(coreMarker);

        if (light.label) {
          glowMarker.bindTooltip(light.label, {
            permanent: false,
            direction: "top",
            className: "zendir-leaflet-tooltip",
          });
        }
      }
    }

    if (internalGrid) {
      const gridStyle = {
        color: "rgba(157, 112, 255, 0.12)",
        weight: 0.5,
        interactive: false,
      };
      [-360, 0, 360].forEach((offset) => {
        for (let lon = -180; lon <= 180; lon += 30) {
          const l = lon + offset;
          addLayer(
            L.polyline(
              [
                [90, l],
                [-90, l],
              ],
              gridStyle,
            ),
          );
        }
        for (let lat = -90; lat <= 90; lat += 30) {
          addLayer(
            L.polyline(
              [
                [lat, -180 + offset],
                [lat, 180 + offset],
              ],
              gridStyle,
            ),
          );
        }
      });
    }

    if (showEquator) {
      [-360, 0, 360].forEach((offset) => {
        addLayer(
          L.polyline(
            [
              [0, -180 + offset],
              [0, 180 + offset],
            ],
            {
              color: "rgba(88, 166, 255, 0.25)",
              weight: 1,
              dashArray: "6, 4",
              interactive: false,
            },
          ),
        );
      });
    }

    groundStations.forEach((gs) => {
      const status = ("status" in gs ? gs.status : undefined) ?? "standby";
      const statusColor = STATUS_COLOR_MAP[status] || STATUS_COLOR_MAP.standby;
      const stationType =
        ("type" in gs ? (gs as { type?: string }).type : undefined) ?? "dish";
      const radius = "coverageRadius" in gs ? gs.coverageRadius : undefined;
      const showCoverage = "showCoverage" in gs ? gs.showCoverage : false;
      if (radius != null && radius > 0 && showCoverage) {
        const ring = geodesicCirclePointsContinuous(
          gs.latitude,
          gs.longitude,
          radius,
          64,
        );
        [0, 360, -360].forEach((offset) => {
          addLayer(
            L.polygon(
              ring.map(([lat, lon]) => [lat, lon + offset] as [number, number]),
              {
                color: `${statusColor}55`,
                fillColor: statusColor,
                fillOpacity: 0.1,
                weight: 1,
                dashArray: "4, 3",
                interactive: false,
              },
            ),
          );
        });
      }

      const gsIcon = createStationDivIcon(statusColor, stationType, status);
      const marker = L.marker([gs.latitude, gs.longitude], { icon: gsIcon });
      addLayer(marker);
      const statusLabel = status !== "standby" ? ` · ${status}` : "";
      marker.bindTooltip(
        `<strong>${gs.name}</strong>${statusLabel}${"network" in gs && gs.network ? `<br/><span style="opacity:0.7">${gs.network}</span>` : ""}`,
        {
          permanent: false,
          direction: "auto",
          className: "zendir-leaflet-tooltip",
        },
      );
      marker.on("click", () => {
        onStationClick?.("id" in gs ? String(gs.id) : gs.name);
      });
    });

    allSatellites.forEach((sat, satIdx) => {
      const track = sat.groundTrack;
      if (!track || track.length === 0) return;

      const color =
        sat.color || DEFAULT_TRACK_COLORS[satIdx % DEFAULT_TRACK_COLORS.length];
      const futureIdx = sat.futureTrackIndex ?? track.length;

      const pastLatLngs = track
        .slice(0, futureIdx)
        .map((p) => [p.latitude, p.longitude] as [number, number]);
      segmentsWithWorldCopies(splitPolylineAtAntimeridian(pastLatLngs)).forEach(
        (seg) => {
          if (seg.length >= 2) {
            addLayer(L.polyline(seg, { color, weight: 2.5, opacity: 1 }));
          }
        },
      );

      if (futureIdx < track.length) {
        const futureLatLngs = track
          .slice(futureIdx)
          .map((p) => [p.latitude, p.longitude] as [number, number]);
        segmentsWithWorldCopies(
          splitPolylineAtAntimeridian(futureLatLngs),
        ).forEach((seg) => {
          if (seg.length >= 2) {
            addLayer(
              L.polyline(seg, {
                color,
                weight: 1.5,
                opacity: 0.5,
                dashArray: "6, 4",
              }),
            );
          }
        });
      }

      // Access mask segments (contact passes) — solid green overlay on track
      if (sat.accessMask && sat.accessMask.some(Boolean)) {
        let segment: [number, number][] = [];
        for (let i = 0; i < track.length; i++) {
          if (sat.accessMask[i]) {
            segment.push([track[i].latitude, track[i].longitude]);
          } else if (segment.length >= 2) {
            segmentsWithWorldCopies(
              splitPolylineAtAntimeridian(segment),
            ).forEach((seg) => {
              if (seg.length >= 2) {
                addLayer(
                  L.polyline(seg, {
                    color: STATUS_COLOR_MAP.normal,
                    weight: 4,
                    opacity: 0.9,
                  }),
                );
              }
            });
            segment = [];
          } else {
            segment = [];
          }
        }
        if (segment.length >= 2) {
          segmentsWithWorldCopies(splitPolylineAtAntimeridian(segment)).forEach(
            (seg) => {
              if (seg.length >= 2) {
                addLayer(
                  L.polyline(seg, {
                    color: STATUS_COLOR_MAP.normal,
                    weight: 4,
                    opacity: 0.9,
                  }),
                );
              }
            },
          );
        }
      }

      if (sat.passMarkers && sat.passMarkers.length > 0) {
        sat.passMarkers.forEach((pm) => {
          const isAos = pm.type === "aos";
          const pmColor = isAos ? "#56f000" : "#ff3838";
          const pmMarker = L.circleMarker([pm.latitude, pm.longitude], {
            radius: 5,
            fillColor: pmColor,
            color: `${pmColor}cc`,
            weight: 1.5,
            fillOpacity: 0.9,
          });
          addLayer(pmMarker);
          pmMarker.bindTooltip(
            `${pm.type.toUpperCase()}${pm.label ? ` – ${pm.label}` : ""}`,
            {
              permanent: false,
              direction: "auto",
              className: "zendir-leaflet-tooltip",
            },
          );
        });
      }

      if (sat.showFootprint && sat.footprintRadius) {
        const lastIdx =
          futureIdx > 0
            ? Math.min(futureIdx - 1, track.length - 1)
            : track.length - 1;
        const last = track[lastIdx];
        const ring = geodesicCirclePointsContinuous(
          last.latitude,
          last.longitude,
          sat.footprintRadius,
          64,
        );
        [0, 360, -360].forEach((offset) => {
          addLayer(
            L.polygon(
              ring.map(([lat, lon]) => [lat, lon + offset] as [number, number]),
              {
                color: `${color}50`,
                fillColor: color,
                fillOpacity: 0.03,
                weight: 1,
                dashArray: "4, 4",
                interactive: false,
              },
            ),
          );
        });
      }

      const currentIdx =
        futureIdx > 0
          ? Math.min(futureIdx - 1, track.length - 1)
          : track.length - 1;
      const current = track[currentIdx];
      const statusColor =
        STATUS_COLOR_MAP[sat.status || "normal"] || STATUS_COLOR_MAP.normal;

      const satIcon = createSatDivIcon(statusColor, color);
      const satMarker = L.marker([current.latitude, current.longitude], {
        icon: satIcon,
      });
      addLayer(satMarker);
      satMarker.bindTooltip(
        `<strong style="color:${color}">${sat.name}</strong><br/>` +
          `Lat ${current.latitude.toFixed(3)}° &nbsp; Lon ${current.longitude.toFixed(3)}°` +
          (current.altitude != null
            ? `<br/>Alt ${current.altitude.toFixed(1)} km`
            : "") +
          (sat.status
            ? `<br/><span style="color:${statusColor}">${sat.status.toUpperCase()}</span>`
            : ""),
        {
          permanent: false,
          direction: "auto",
          className: "zendir-leaflet-tooltip",
        },
      );
      satMarker.on("click", () => onSatelliteClick?.(sat.id));
    });

    if (showLegend && (allSatellites.length > 0 || groundStations.length > 0)) {
      const Legend = L.Control.extend({
        onAdd() {
          const div = L.DomUtil.create("div", "leaflet-legend-zendir");
          div.style.cssText = `
            padding: 8px 12px; background: ${tokens.colors.background.surface}; border: 1px solid ${tokens.colors.border.default};
            border-radius: 8px; color: ${tokens.colors.text.primary}; font-size: 11px; font-family: Roboto, sans-serif;
          `;
          allSatellites.forEach((s, i) => {
            const c =
              s.color || DEFAULT_TRACK_COLORS[i % DEFAULT_TRACK_COLORS.length];
            const row = document.createElement("div");
            row.style.cssText =
              "display: flex; align-items: center; gap: 6px; margin: 2px 0;";
            const swatch = document.createElement("span");
            swatch.style.cssText = `width:8px;height:8px;border-radius:50%;background:${c};`;
            const label = document.createElement("span");
            label.textContent = s.name;
            row.appendChild(swatch);
            row.appendChild(label);
            div.appendChild(row);
          });
          if (groundStations.length > 0) {
            const row = document.createElement("div");
            row.style.cssText =
              "display: flex; align-items: center; gap: 6px; margin: 2px 0; margin-top: 6px;";
            const swatch = document.createElement("span");
            swatch.style.cssText = `width:8px;height:8px;border-radius:50%;background:${STATUS_COLOR_MAP.standby};`;
            const label = document.createElement("span");
            label.textContent = `${groundStations.length} Ground Station${groundStations.length > 1 ? "s" : ""}`;
            row.appendChild(swatch);
            row.appendChild(label);
            div.appendChild(row);
          }
          return div;
        },
      });
      const legend = new Legend({ position: "topleft" });
      legend.addTo(map);
      controlsRef.current.push(legend);
    }
  }, [
    ready,
    allSatellites,
    groundStations,
    internalTerminator,
    terminatorTime,
    nightTileUrl,
    lightSources,
    internalGrid,
    showEquator,
    showLegend,
    tileStyle,
    tokens.colors.text.primary,
    tokens.colors.background.surface,
    tokens.colors.border.default,
    addLayer,
    clearLayers,
    onSatelliteClick,
    onStationClick,
  ]);

  // ==========================================================================
  // Pins layer — separate from overlayGroup so pin updates don't rebuild tracks
  // ==========================================================================

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const group = pinsGroupRef.current;
    if (!group) return;
    group.clearLayers();

    if (!pins || pins.length === 0) return;
    const accentColor = tokens.colors.accent.primary;

    pins.forEach((pin) => {
      const pinColor = pin.color || accentColor;

      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
        <defs>
          <filter id="pin-shadow-${pin.id}" x="-30%" y="-10%" width="160%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.35"/>
          </filter>
        </defs>
        <g filter="url(#pin-shadow-${pin.id})">
          <path d="M12 2 C6.5 2 2 6.5 2 12 C2 19 12 34 12 34 C12 34 22 19 22 12 C22 6.5 17.5 2 12 2Z"
                fill="${pinColor}" stroke="rgba(255,255,255,0.5)" stroke-width="1"/>
          <circle cx="12" cy="12" r="4" fill="#0d1323" stroke="rgba(255,255,255,0.7)" stroke-width="0.8"/>
        </g>
      </svg>`;

      const icon = L.divIcon({
        html: svg,
        className: "zendir-pin-icon",
        iconSize: [24, 36] as unknown as L.PointExpression,
        iconAnchor: [12, 36] as unknown as L.PointExpression,
        tooltipAnchor: [0, -36] as unknown as L.PointExpression,
      });

      const marker = L.marker([pin.latitude, pin.longitude], {
        icon,
        draggable: pinsEditable,
        title: pin.label ?? "",
      });

      // Rich tooltip
      const tooltipLines = [
        pin.label ? `<strong>${pin.label}</strong>` : "",
        pin.description
          ? `<div style="opacity:0.7;font-size:11px">${pin.description}</div>`
          : "",
        `<div style="font-size:10px;opacity:0.5;font-family:monospace">${pin.latitude.toFixed(4)}°, ${pin.longitude.toFixed(4)}°</div>`,
        pin.createdBy
          ? `<div style="font-size:10px;opacity:0.4;margin-top:2px">by ${pin.createdBy}</div>`
          : "",
      ]
        .filter(Boolean)
        .join("");
      marker.bindTooltip(tooltipLines, {
        className: "zendir-pin-tooltip",
        direction: "top",
        offset: [0, -4],
      });

      if (pinsEditable) {
        // Drag to reposition
        marker.on("dragend", () => {
          const latlng = marker.getLatLng();
          onPinUpdate?.({
            ...pin,
            latitude: latlng.lat,
            longitude: latlng.lng,
          });
        });

        // Right-click to delete
        marker.on("contextmenu", (e: L.LeafletEvent) => {
          L.DomEvent.stopPropagation(e as unknown as Event);
          onPinRemove?.(pin.id);
        });
      }

      group.addLayer(marker);
    });
  }, [
    ready,
    pins,
    pinsEditable,
    onPinUpdate,
    onPinRemove,
    tokens.colors.accent.primary,
  ]);

  // Click-to-add: listen for clicks on the map background when pinsEditable is true
  useEffect(() => {
    if (!ready || !mapRef.current || !pinsEditable || !onPinAdd) return;
    const map = mapRef.current;

    const handleClick = (e: L.LeafletMouseEvent) => {
      // Only fire on map background clicks — ignore clicks that bubbled from markers
      if (
        (e.originalEvent?.target as HTMLElement)?.closest?.(
          ".leaflet-marker-icon",
        )
      )
        return;
      onPinAdd({
        latitude: e.latlng.lat,
        longitude: e.latlng.lng,
        label: "",
        color: tokens.colors.accent.primary,
      });
    };

    map.on("click", handleClick);
    return () => {
      map.off("click", handleClick);
    };
  }, [ready, pinsEditable, onPinAdd, tokens.colors.accent.primary]);

  const handleRecenter = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const points: L.LatLngExpression[] = [];
    allSatellites.forEach((s) => {
      s.groundTrack.forEach((p) => points.push([p.latitude, p.longitude]));
    });
    groundStations.forEach((gs) => points.push([gs.latitude, gs.longitude]));
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
    } else {
      map.setView(defaultCenter, defaultZoom);
    }
  }, [allSatellites, groundStations, defaultCenter, defaultZoom]);

  const handleZoomIn = useCallback(() => {
    mapRef.current?.zoomIn();
  }, []);
  const handleZoomOut = useCallback(() => {
    mapRef.current?.zoomOut();
  }, []);

  const resolvedMinHeight =
    minHeight || (typeof height === "number" ? `${height}px` : "400px");

  const isEmpty =
    allSatellites.length === 0 &&
    groundStations.length === 0 &&
    (!pins || pins.length === 0);

  // ── Shared control button style ──
  const ctrlBtnBase: React.CSSProperties = {
    background: "rgba(20, 24, 38, 0.92)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: "1px solid rgba(120, 100, 180, 0.18)",
    color: "#c8c0d8",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s, border-color 0.15s",
  };

  const ctrlHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = "rgba(30, 34, 52, 0.95)";
    e.currentTarget.style.borderColor = "rgba(157, 112, 255, 0.4)";
  };
  const ctrlLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = "rgba(20, 24, 38, 0.92)";
    e.currentTarget.style.borderColor = "rgba(120, 100, 180, 0.18)";
  };

  // Overlay items shown in the Layers panel
  const overlayItems: Array<{ id: string; label: string; enabled: boolean }> =
    [];
  overlayItems.push({
    id: "terminator",
    label: "Day / Night",
    enabled: internalTerminator,
  });
  overlayItems.push({ id: "grid", label: "Grid Lines", enabled: internalGrid });
  for (const l of customLayers ?? []) {
    overlayItems.push({
      id: l.id,
      label: l.label,
      enabled: customLayerState[l.id] ?? l.defaultEnabled ?? true,
    });
  }

  return (
    <div
      className={`zendir-ground-track-map zendir-ground-track-map-leaflet${pinsEditable ? " zendir-pins-editable" : ""} ${className}`}
      data-map-backend="leaflet"
      style={{
        width,
        height,
        minHeight: resolvedMinHeight,
        backgroundColor: tokens.colors.background.base,
        borderRadius: 8,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100%", minHeight: resolvedMinHeight }}
      />
      {isEmpty && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: tokens.colors.text.secondary,
            fontSize: 14,
            pointerEvents: "none",
          }}
        >
          {emptyMessage}
        </div>
      )}
      {/* ── Unified Map Controls — top-right control bar ── */}
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 1000,
          display: "flex",
          flexDirection: "row",
          gap: 4,
          alignItems: "flex-start",
          pointerEvents: "none",
        }}
      >
        {/* Recenter */}
        {showRecenterButton && (
          <button
            type="button"
            onClick={handleRecenter}
            title="Recenter map to fit all assets and ground stations"
            aria-label="Recenter map"
            style={{
              ...ctrlBtnBase,
              borderRadius: 4,
              padding: "6px 8px",
              gap: 4,
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: "0.03em",
              pointerEvents: "auto",
            }}
            onMouseEnter={ctrlHover}
            onMouseLeave={ctrlLeave}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
            </svg>
          </button>
        )}

        {/* Layers panel */}
        <div
          ref={layersPanelRef}
          style={{ position: "relative", pointerEvents: "auto" }}
        >
          <button
            type="button"
            onClick={() => setLayersPanelOpen((o) => !o)}
            title="Map layers"
            aria-label="Toggle map layers panel"
            aria-expanded={layersPanelOpen}
            style={{
              ...ctrlBtnBase,
              borderRadius: 4,
              padding: "6px 8px",
              pointerEvents: "auto",
              borderColor: layersPanelOpen
                ? "rgba(157, 112, 255, 0.4)"
                : ctrlBtnBase.border
                  ? undefined
                  : "rgba(120, 100, 180, 0.18)",
              background: layersPanelOpen
                ? "rgba(30, 34, 52, 0.95)"
                : ctrlBtnBase.background,
            }}
            onMouseEnter={ctrlHover}
            onMouseLeave={ctrlLeave}
          >
            {/* Layers icon — stacked diamonds */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </button>

          {/* Dropdown panel */}
          {layersPanelOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                minWidth: 180,
                background: "rgba(16, 18, 30, 0.96)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1px solid rgba(120, 100, 180, 0.18)",
                borderRadius: 6,
                padding: "8px 0",
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              }}
            >
              {/* Base Map section (only when no explicit tileUrl) */}
              {!isExplicitTileUrl && (
                <>
                  <div
                    style={{
                      padding: "4px 12px 6px",
                      fontSize: 9,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase" as const,
                      color: "#7a748e",
                    }}
                  >
                    Base Map
                  </div>
                  <div
                    style={{ display: "flex", gap: 2, padding: "0 8px 8px" }}
                  >
                    {(["dark", "satellite"] as const).map((style) => (
                      <button
                        key={style}
                        type="button"
                        onClick={() => handleTileStyleChange(style)}
                        aria-pressed={tileStyle === style}
                        style={{
                          flex: 1,
                          background:
                            tileStyle === style
                              ? "rgba(157, 112, 255, 0.18)"
                              : "rgba(255,255,255,0.03)",
                          border: `1px solid ${tileStyle === style ? "rgba(157, 112, 255, 0.35)" : "rgba(120, 100, 180, 0.1)"}`,
                          borderRadius: 4,
                          color: tileStyle === style ? "#d0c4ee" : "#7a748e",
                          cursor: "pointer",
                          padding: "5px 6px",
                          fontSize: 10,
                          fontWeight: tileStyle === style ? 600 : 400,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          transition: "all 0.15s ease",
                        }}
                      >
                        {style === "dark" ? (
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            stroke="none"
                          >
                            <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                          </svg>
                        ) : (
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                          </svg>
                        )}
                        {style === "dark" ? "Dark" : "Satellite"}
                      </button>
                    ))}
                  </div>
                  <div
                    style={{
                      height: 1,
                      background: "rgba(120, 100, 180, 0.1)",
                      margin: "0 8px",
                    }}
                  />
                </>
              )}

              {/* Overlays section */}
              <div
                style={{
                  padding: `${isExplicitTileUrl ? "4px" : "8px"} 12px 4px`,
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase" as const,
                  color: "#7a748e",
                }}
              >
                Overlays
              </div>
              {overlayItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleLayerToggle(item.id, !item.enabled)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "5px 12px",
                    background: "transparent",
                    border: "none",
                    color: item.enabled ? "#c8c0d8" : "#5a5470",
                    cursor: "pointer",
                    fontSize: 11,
                    textAlign: "left" as const,
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "rgba(157, 112, 255, 0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {/* Toggle indicator */}
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      border: `1.5px solid ${item.enabled ? "rgba(157, 112, 255, 0.6)" : "rgba(120, 100, 180, 0.25)"}`,
                      background: item.enabled
                        ? "rgba(157, 112, 255, 0.22)"
                        : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.15s ease",
                    }}
                  >
                    {item.enabled && (
                      <svg
                        width="9"
                        height="9"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="#d0c4ee"
                        strokeWidth="2"
                      >
                        <path d="M2 6l3 3 5-5" />
                      </svg>
                    )}
                  </span>
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Zoom controls */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            pointerEvents: "auto",
          }}
        >
          <button
            type="button"
            onClick={handleZoomIn}
            title="Zoom in"
            aria-label="Zoom in"
            style={{
              ...ctrlBtnBase,
              borderRadius: "4px 4px 0 0",
              borderBottom: "none",
              width: 30,
              height: 28,
              fontSize: 16,
              fontWeight: 300,
              pointerEvents: "auto",
            }}
            onMouseEnter={ctrlHover}
            onMouseLeave={ctrlLeave}
          >
            +
          </button>
          <button
            type="button"
            onClick={handleZoomOut}
            title="Zoom out"
            aria-label="Zoom out"
            style={{
              ...ctrlBtnBase,
              borderRadius: "0 0 4px 4px",
              width: 30,
              height: 28,
              fontSize: 16,
              fontWeight: 300,
              pointerEvents: "auto",
            }}
            onMouseEnter={ctrlHover}
            onMouseLeave={ctrlLeave}
          >
            −
          </button>
        </div>
      </div>
    </div>
  );
}

export default GroundTrackMapLeaflet;
